import React, { ComponentType, useCallback, useMemo, useState } from 'react'
//models
import { CalendarProps, DragAndDropCalendarProps } from './Calendar.props'

import {
  Calendar as BigCalendar,
  momentLocalizer,
  CalendarProps as BigCalendarProps,
} from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import moment from 'moment'
import { useEffect } from 'react'
import { findIndex } from 'lodash'

import { CalendarContainer } from './Calendar.styled'

import { EventCalendar } from 'types/event-calendar-types'
import CalendarToolbar from './CalendarToolbar'

const DnDCalendar = withDragAndDrop(
  BigCalendar as ComponentType<BigCalendarProps>
)

const Calendar: React.FC<CalendarProps> = ({
  draggable,
  events,
  columns,
  defaultView = 'day',
  toolbar,
  showDoubleTime = false,
  selectable,
  defaultDate,
  onSelected,
  onSelectedDate,
  onSelectEvent,
  onEventDrop,
  onEventDropEventCalendar,
  onResize,
}) => {
  moment.locale('en')

  const localizer = useMemo(() => momentLocalizer(moment), [])
  const calendarTimeColumn = document.getElementsByClassName(
    'rbc-time-gutter rbc-time-column'
  )

  const calendarTimeHeader = document.getElementsByClassName(
    'rbc-label rbc-time-header-gutter'
  )
  const calendarTimeColumnElem = document.getElementById('calendar-time-column')
  const calendarContainer = document.getElementsByClassName('rbc-time-view')
  const timeHeaderContainer = document.getElementsByClassName('rbc-time-header')
  const timeColumnContainer = document.getElementsByClassName(
    'rbc-time-content'
  )
  const calendarSecondTimeColumn = document.getElementById('time_column')
  const [view] = useState<'month' | 'week' | 'work_week' | 'day' | 'agenda'>(
    defaultView
  )
  const [domLoad, setDomLoad] = useState<boolean>(false)
  const [date, setDate] = useState<Date>(defaultDate ?? new Date())
  const [calendarEvents, setCalendarEvents] = useState<EventCalendar[] | []>(
    events
  )
  const calendarDate = new Date()
  calendarDate.setHours(6)

  calendarDate.setMinutes(0)
  calendarDate.setMilliseconds(0)

  const colors: { [key: string]: string } = {
    primary: '#1600FF',
    secondaryDisabled: 'rgba(244, 83, 83, 0.5)',
  }

  const eventStyleGetter = useCallback(
    (event: EventCalendar) => {
      let { color } = event
      const { textColor } = event
      color = colors[color] ? colors[color] : color
      const style = {
        backgroundColor: color ? color : undefined,
        color: textColor ? textColor : undefined,
        border: '1.5px solid white',
        borderRadius: '0px',
        opacity: 1,
        display: 'block',
      }
      return {
        style: style,
      }
    },
    [colors]
  ) //

  useEffect(() => {
    if (
      showDoubleTime &&
      calendarTimeColumn &&
      calendarTimeHeader &&
      timeColumnContainer &&
      timeHeaderContainer
    ) {
      calendarTimeColumn[0].setAttribute('id', 'calendar-time-column')
      const clonedTimeColumn = calendarTimeColumn[0].cloneNode(true)
      const clonedHeader = calendarTimeHeader[0].cloneNode(true)
      timeColumnContainer[0].appendChild(clonedTimeColumn)
      timeHeaderContainer[0].appendChild(clonedHeader)
    }
  }, [
    showDoubleTime,
    calendarTimeColumn,
    calendarSecondTimeColumn,
    showDoubleTime,
  ])

  useEffect(() => {
    if (calendarTimeColumnElem) {
      const dynamicWidth = calendarTimeColumnElem.offsetWidth
      if (dynamicWidth) {
        calendarTimeHeader[1].setAttribute(
          'style',
          `width: ${dynamicWidth}px; min-width: ${dynamicWidth}px; max-width: ${dynamicWidth}px;`
        )
        calendarTimeHeader[1].setAttribute(
          'class',
          'rbc-label rbc-time-header-gutter addBorder'
        )
      }
    }
  }, [calendarTimeColumnElem])

  useEffect(() => {
    if (showDoubleTime && !domLoad) {
      setTimeout(() => setDomLoad(true), 100)
    }
    if (showDoubleTime && calendarContainer && calendarContainer[0]) {
      calendarContainer[0].setAttribute('class', 'rbc-time-view ')
    }
  }, [showDoubleTime, domLoad, calendarContainer])

  useEffect(() => {
    if (events) {
      setCalendarEvents(events)
    }
  }, [events])

  useEffect(() => {
    if (onSelectedDate && date) {
      onSelectedDate(date)
    }
  }, [date])

  useEffect(() => {
    if (defaultDate) {
      setDate(defaultDate)
    }
  }, [defaultDate])

  const moveEvent = useCallback(
    (currentEvent: DragAndDropCalendarProps) => {
      const { event, start, end, resourceId } = currentEvent

      if (
        event &&
        event.courts &&
        event.resourceIds &&
        calendarEvents.length > 0
      ) {
        const idx: number = findIndex(calendarEvents, { id: event.id })
        const newResourceId = parseInt(resourceId)
        const updatedCourts = [...event.courts]
        const updatedResourceIds = [...event.resourceIds]

        const oldResourceIdIndex = updatedResourceIds.indexOf(
          parseInt(event.resourceId)
        )
        if (oldResourceIdIndex !== -1) {
          updatedResourceIds.splice(oldResourceIdIndex, 1)
          updatedCourts.splice(
            updatedCourts.indexOf(parseInt(event.resourceId)),
            1
          )
        }
        if (!updatedResourceIds.includes(newResourceId)) {
          updatedResourceIds.push(newResourceId)
          updatedCourts.push(newResourceId)
        }

        const updatedEvent = {
          ...event,
          start,
          end,
          resourceId,
          resourceIds: updatedResourceIds,
          courts: updatedCourts,
        }
        const nextEvents = [...calendarEvents]
        nextEvents.splice(idx, 1, updatedEvent)
        setCalendarEvents(nextEvents)
        if (onEventDropEventCalendar) {
          return onEventDropEventCalendar(nextEvents[0])
        }
        if (onEventDrop) {
          return onEventDrop(currentEvent)
        }
      }
    },
    [calendarEvents, onEventDropEventCalendar, onEventDrop]
  )

  const resizeEvent = useCallback(
    (currentEvent: DragAndDropCalendarProps) => {
      const { event, start, end } = currentEvent
      const nextEvents = calendarEvents.map((existingEvent: EventCalendar) => {
        return existingEvent.id === event.id
          ? { ...existingEvent, start, end }
          : existingEvent
      })
      if (onResize) {
        onResize(currentEvent)
      }
      setCalendarEvents(nextEvents)
    },
    [calendarEvents, onResize]
  )

  return (
    <CalendarContainer>
      {toolbar && (
        <CalendarToolbar date={date} onDate={(date: Date) => setDate(date)} />
      )}
      {!draggable ? (
        <BigCalendar
          localizer={localizer}
          style={{ height: '670px', width: '100%' }}
          toolbar={false}
          events={calendarEvents}
          step={60}
          view={view}
          resources={columns}
          date={date}
          onNavigate={date => setDate(date)}
          onSelectSlot={slot => {
            console.log('slot select: ', slot)
          }}
          onSelectEvent={onSelectEvent}
          eventPropGetter={eventStyleGetter}
        />
      ) : (
        <div style={{ display: 'flex' }}>
          {/*@ts-ignore*/}
          <DnDCalendar
            selectable={selectable}
            localizer={localizer}
            style={{ height: '670px', width: showDoubleTime ? '100%' : '100%' }}
            toolbar={false}
            events={calendarEvents}
            min={calendarDate}
            view={view}
            resources={columns}
            date={date}
            onNavigate={date => setDate(date)}
            eventPropGetter={eventStyleGetter}
            onEventDrop={moveEvent}
            onEventResize={resizeEvent}
            onSelectSlot={slot => {
              if (onSelected) onSelected(slot)
            }}
            onSelectEvent={onSelectEvent}
            resizable
            className="calendarLastBorder"
          />
        </div>
      )}
    </CalendarContainer>
  )
}

export default Calendar
